// ─── Toast.jsx ────────────────────────────────────────────────────────────────
// Lightweight toast notification system.
// Usage:
//   const { toasts, showToast } = useToast();
//   showToast("Saved!", "success");
//   <ToastContainer toasts={toasts} />
import { useState, useCallback } from "react";
import { useTheme } from "./theme";

let _id = 0;

export function useToast() {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "info", duration = 3500) => {
    const id = ++_id;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return { toasts, showToast, dismissToast };
}

export function ToastContainer({ toasts, onDismiss }) {
  const C = useTheme();

  const typeStyles = {
    success: { bg: "rgba(0,230,118,0.12)", border: "rgba(0,230,118,0.4)", icon: "✓", color: "#00e676" },
    error:   { bg: "rgba(255,68,68,0.12)",  border: "rgba(255,68,68,0.4)",  icon: "✕", color: "#ff4444" },
    info:    { bg: "rgba(0,180,255,0.12)",  border: "rgba(0,180,255,0.4)",  icon: "ℹ", color: "#00b4ff" },
    warn:    { bg: "rgba(255,180,0,0.12)",  border: "rgba(255,180,0,0.4)",  icon: "⚠", color: "#ffb400" },
  };

  if (!toasts?.length) return null;

  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24,
      display: "flex", flexDirection: "column", gap: 10,
      zIndex: 9999, pointerEvents: "none",
    }}>
      {toasts.map(toast => {
        const s = typeStyles[toast.type] || typeStyles.info;
        return (
          <div key={toast.id} style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "12px 16px",
            background: s.bg,
            border: `1px solid ${s.border}`,
            borderRadius: 10,
            fontFamily: "'DM Mono', monospace",
            fontSize: 13,
            color: C.text,
            boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
            minWidth: 260, maxWidth: 380,
            pointerEvents: "all",
            animation: "fadeSlideIn 0.2s ease",
          }}>
            <span style={{ color: s.color, fontWeight: 700, flexShrink: 0 }}>{s.icon}</span>
            <span style={{ flex: 1, lineHeight: 1.4 }}>{toast.message}</span>
            <button
              onClick={() => onDismiss?.(toast.id)}
              style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer",
                fontSize: 14, padding: "0 2px", lineHeight: 1, flexShrink: 0 }}>✕</button>
          </div>
        );
      })}
      <style>{`@keyframes fadeSlideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
